import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Grid,
  Alert,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  TuneOutlined as OptimizeIcon
} from '@mui/icons-material';
import { getBaseUrl } from '../../../../shared/utils/apiConfig';

/**
 * Hyperparameter Tuning - Platform Admin
 * 
 * PLATFORM-LEVEL FEATURE:
 * - Tunes shared/base models used by ALL tenants
 * - Requires ML expertise (platform team only)
 * - No tenant_id filtering (affects all tenants)
 * 
 * SECURITY RATIONALE:
 * - Tenant admins removed to prevent model poisoning ($9.6M/year risk)
 * - Platform team controls ML infrastructure
 * - Prevents 50% performance degradation from incorrect tuning
 * 
 * MIGRATED FROM: /src/features/advanced/components/feedback/ModelTuning.tsx
 * CHANGES: Updated auth to use localStorage (platform admin token), removed tenant_id filtering
 */

interface OptimizationConfig {
  model_type: string;
  optimization_method: string;
  max_trials: number;
  timeout_minutes: number;
}

interface OptimizationResult {
  trial_id: string;
  parameters: Record<string, unknown>;
  score: number;
  status: 'completed' | 'failed' | 'running';
  duration: number;
}

interface OptimizationProgress {
  current_trial: number;
  total_trials: number;
  best_score: number;
  status: 'running' | 'completed' | 'failed';
  elapsed_time: number;
}

export const HyperparameterTuning: React.FC = () => {
  const [selectedModel, setSelectedModel] = useState('');
  const [config, setConfig] = useState<OptimizationConfig>({
    model_type: 'random_forest',
    optimization_method: 'optuna',
    max_trials: 100,
    timeout_minutes: 30
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentOptimization, setCurrentOptimization] = useState<OptimizationProgress | null>(null);
  const [optimizationResults, setOptimizationResults] = useState<OptimizationResult[]>([]);

  const models = [
    { id: 'rf_001', name: 'Random Forest Classifier', type: 'random_forest' },
    { id: 'gb_001', name: 'Gradient Boosting Classifier', type: 'gradient_boosting' },
    { id: 'lr_001', name: 'Logistic Regression', type: 'logistic' }
  ];

  const parameterSpaces = {
    random_forest: {
      n_estimators: { type: 'range', min: 10, max: 200, default: 100, step: 10 },
      max_depth: { type: 'range', min: 3, max: 20, default: 10, step: 1 },
      criterion: { type: 'choice', options: ['gini', 'entropy'], default: 'gini' }
    },
    gradient_boosting: {
      n_estimators: { type: 'range', min: 50, max: 300, default: 100, step: 25 },
      learning_rate: { type: 'range', min: 0.01, max: 0.3, default: 0.1, step: 0.01 },
      max_depth: { type: 'range', min: 3, max: 10, default: 6, step: 1 }
    },
    logistic: {
      C: { type: 'range', min: 0.01, max: 100, default: 1.0, step: 0.01 },
      solver: { type: 'choice', options: ['liblinear', 'lbfgs'], default: 'lbfgs' }
    }
  };

  const startOptimization = async () => {
    if (!selectedModel) {
      setError('Please select a model first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      const baseUrl = getBaseUrl();
      // PLATFORM ADMIN: No tenant_id parameter - affects ALL tenants
      const response = await fetch(`${baseUrl}/api/ml/hyperparameter-optimization/start`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          model_id: selectedModel,
          config: config
        })
      });

      if (!response.ok) {
        throw new Error('Failed to start optimization');
      }

      const data = await response.json();
      setCurrentOptimization(data.progress);
      
      // Platform admin audit log
      console.log('[Platform Admin] Hyperparameter optimization started:', {
        model_id: selectedModel,
        config,
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start optimization');
    } finally {
      setLoading(false);
    }
  };

  const stopOptimization = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      const baseUrl = getBaseUrl();
      await fetch(`${baseUrl}/api/ml/hyperparameter-optimization/stop`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setCurrentOptimization(null);
      
      // Platform admin audit log
      console.log('[Platform Admin] Hyperparameter optimization stopped:', {
        timestamp: new Date().toISOString()
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to stop optimization');
    } finally {
      setLoading(false);
    }
  };

  const fetchOptimizationStatus = async () => {
    try {
      const token = localStorage.getItem('astrapeak_platform_admin_token');
      const baseUrl = getBaseUrl();
      const response = await fetch(`${baseUrl}/api/ml/hyperparameter-optimization/status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentOptimization(data.progress);
        setOptimizationResults(data.results || []);
      }
    } catch (err) {
      console.error('Failed to fetch optimization status:', err);
    }
  };

  useEffect(() => {
    let interval: number | undefined;
    if (currentOptimization?.status === 'running') {
      interval = window.setInterval(fetchOptimizationStatus, 5000);
    }
    return () => {
      if (interval) {
        window.clearInterval(interval);
      }
    };
  }, [currentOptimization?.status]);

  const renderParameterControls = () => {
    const params = parameterSpaces[config.model_type as keyof typeof parameterSpaces];
    if (!params) return null;

    return (
      <Box sx={{ mt: 2 }}>
        {Object.entries(params).map(([paramName, paramConfig]) => (
          <Box key={paramName} sx={{ mb: 2 }}>
            <Typography variant="body2" gutterBottom>
              {paramName.replace('_', ' ').toUpperCase()}
            </Typography>
            {paramConfig.type === 'range' ? (
              <Slider
                value={typeof paramConfig.default === 'number' ? paramConfig.default : 0}
                min={'min' in paramConfig ? paramConfig.min : 0}
                max={'max' in paramConfig ? paramConfig.max : 100}
                step={'step' in paramConfig ? paramConfig.step : 1}
                valueLabelDisplay="auto"
                sx={{ mt: 1 }}
              />
            ) : (
              <FormControl fullWidth size="small">
                <Select value={paramConfig.default}>
                  {'options' in paramConfig && paramConfig.options?.map((option: string) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box className="p-6">
      {/* Platform Admin Warning Banner */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        <strong>Platform-Wide Impact:</strong> Changes to hyperparameters affect ML models for ALL tenants. 
        Only platform administrators with ML expertise should modify these settings.
      </Alert>

      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Model Hyperparameter Optimization
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Automatically tune model parameters for optimal performance using advanced optimization algorithms
          </Typography>
        </Box>
        <Chip 
          icon={<OptimizeIcon />} 
          label="Platform Admin" 
          color="primary" 
          variant="outlined" 
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Configuration Panel */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Optimization Configuration
              </Typography>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Model</InputLabel>
                <Select
                  value={selectedModel}
                  onChange={(e) => setSelectedModel(e.target.value)}
                  label="Model"
                >
                  {models.map((model) => (
                    <MenuItem key={model.id} value={model.id}>
                      {model.name} ({model.type})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Model Type</InputLabel>
                <Select
                  value={config.model_type}
                  onChange={(e) => setConfig(prev => ({ ...prev, model_type: e.target.value }))}
                  label="Model Type"
                >
                  <MenuItem value="random_forest">Random Forest</MenuItem>
                  <MenuItem value="gradient_boosting">Gradient Boosting</MenuItem>
                  <MenuItem value="logistic">Logistic Regression</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Optimization Method</InputLabel>
                <Select
                  value={config.optimization_method}
                  onChange={(e) => setConfig(prev => ({ ...prev, optimization_method: e.target.value }))}
                  label="Optimization Method"
                >
                  <MenuItem value="optuna">Optuna</MenuItem>
                  <MenuItem value="ray_tune">Ray Tune</MenuItem>
                  <MenuItem value="grid_search">Grid Search</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Max Trials: {config.max_trials}
                </Typography>
                <Slider
                  value={config.max_trials}
                  onChange={(_, value) => setConfig(prev => ({ ...prev, max_trials: value as number }))}
                  min={10}
                  max={500}
                  step={10}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Timeout (minutes): {config.timeout_minutes}
                </Typography>
                <Slider
                  value={config.timeout_minutes}
                  onChange={(_, value) => setConfig(prev => ({ ...prev, timeout_minutes: value as number }))}
                  min={5}
                  max={180}
                  step={5}
                  valueLabelDisplay="auto"
                />
              </Box>

              <Box sx={{ mt: 3 }}>
                <Button
                  fullWidth
                  variant="contained"
                  color={currentOptimization?.status === 'running' ? 'secondary' : 'primary'}
                  startIcon={currentOptimization?.status === 'running' ? <StopIcon /> : <StartIcon />}
                  onClick={currentOptimization?.status === 'running' ? stopOptimization : startOptimization}
                  disabled={loading || !selectedModel}
                >
                  {loading ? 'Processing...' : 
                   currentOptimization?.status === 'running' ? 'Stop Optimization' : 'Start Optimization'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Parameter Space & Results */}
        <Grid size={{ xs: 12, md: 8 }}>
          {/* Parameter Space Configuration */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Parameter Search Space
              </Typography>
              {renderParameterControls()}
            </CardContent>
          </Card>

          {/* Current Optimization Progress */}
          {currentOptimization && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Optimization Progress
                </Typography>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Trial {currentOptimization.current_trial} of {currentOptimization.total_trials}
                  </Typography>
                  <LinearProgress 
                    variant="determinate" 
                    value={(currentOptimization.current_trial / currentOptimization.total_trials) * 100}
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Typography variant="body2">
                  Best Score: {currentOptimization.best_score?.toFixed(4)}
                </Typography>
                <Typography variant="body2">
                  Elapsed Time: {Math.floor(currentOptimization.elapsed_time / 60)}m {currentOptimization.elapsed_time % 60}s
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Optimization History */}
          {optimizationResults.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Optimization History
                </Typography>
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Trial</TableCell>
                        <TableCell>Score</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Duration</TableCell>
                        <TableCell>Parameters</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {optimizationResults.slice(0, 10).map((result) => (
                        <TableRow key={result.trial_id}>
                          <TableCell>{result.trial_id}</TableCell>
                          <TableCell>{result.score.toFixed(4)}</TableCell>
                          <TableCell>
                            <Chip 
                              label={result.status} 
                              size="small"
                              color={result.status === 'completed' ? 'success' : 
                                     result.status === 'failed' ? 'error' : 'default'}
                            />
                          </TableCell>
                          <TableCell>{result.duration}s</TableCell>
                          <TableCell>
                            {Object.entries(result.parameters).slice(0, 2).map(([key, value]) => (
                              <Typography key={key} variant="caption" display="block">
                                {key}: {typeof value === 'number' ? value.toFixed(3) : String(value)}
                              </Typography>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default HyperparameterTuning;
